import { useEffect, useRef } from "react";
import { isTopLayer } from "../../utils";

/**
 * Props for the useOnClickOutside hook
 */
type UseOnClickOutsideProps = {
  /**
   * Callback fired when a click begins outside target elements (on pointerdown)
   */
  onClickOutsideBegin?: (e: PointerEvent) => void;
  /**
   * Callback fired when a click completes outside target elements (on pointerup),
   * but only if the click also began outside the targets
   */
  onClickOutside?: (e: PointerEvent) => void;
  /**
   * Array of target elements to monitor
   */
  targets: (HTMLElement | undefined)[];
  /**
   * Whether to disable the useOnClickOutside hook
   */
  disable?: boolean;
  /**
   * Optional layer ID for stacking management. When provided, callbacks will only
   * fire if this layer is the topmost layer in the stack.
   */
  layerId?: string;
};

/**
 * Custom hook for detecting clicks outside of specified target elements.
 *
 * Features:
 * - Detects clicks outside of multiple target elements
 * - Supports both pointerdown and pointerup event handling (works for mouse and touch)
 * - Provides separate callbacks for click begin and complete actions
 * - Handles SSR environments gracefully
 * - Automatically cleans up event listeners
 * - Uses composedPath for accurate event target detection
 * - Supports optional callbacks for flexible usage
 *
 * @param props - Configuration object containing targets and optional callbacks
 * @returns void
 */
export const useOnClickOutside = ({
  onClickOutsideBegin,
  targets,
  onClickOutside,
  disable,
  layerId,
}: UseOnClickOutsideProps) => {
  const pressStartedOutsideRef = useRef(false);

  useEffect(() => {
    if (disable || typeof document === "undefined") return;

    const isOutsideTargets = (e: PointerEvent) => {
      const path = e.composedPath();
      return !targets.some((el) => el && path.includes(el));
    };

    const handlePointerDown = (e: PointerEvent) => {
      const isOutside = isOutsideTargets(e);
      pressStartedOutsideRef.current = isOutside;

      if (isOutside && (!layerId || isTopLayer(layerId))) {
        onClickOutsideBegin?.(e);
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      const startedOutside = pressStartedOutsideRef.current;
      pressStartedOutsideRef.current = false; // Reset for next interaction

      if (startedOutside && isOutsideTargets(e)) {
        if (!layerId || isTopLayer(layerId)) {
          onClickOutside?.(e);
        }
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("pointerup", handlePointerUp);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [onClickOutside, onClickOutsideBegin, targets, disable, layerId]);
};
